import axios from 'axios';
import { PropTypes } from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { ANALYTICS_API_URL, CTS_API_URL } from '../../../../constants/config';
import * as browserStorage from '../../../../misc/BrowserStorageManager';
import { Notification } from '../../../../styledComponents/styledComponents';
import { useQuery } from '@tanstack/react-query';
import { getUnifiedErrorObject } from '../../../../util/ErrorUnifier';
import styled, { keyframes } from 'styled-components';
import StreamMetricsDisplay from './MetricsContainer';
import { getOrgaHashOfStream, selectStreamAlerts } from './streamAlerts';
import { selectRenditions } from './transcodes';
import moment from 'moment';

export const Container = styled.div`
   font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
   background: #f5f5f5;
   min-height: 100vh;
   display: flex;
   align-items: center;
   justify-content: center;
   padding: 20px 10%;
   flex-direction: column;
`;
Container.displayName = 'Container';

const slideIn = keyframes`
  from { transform: translateY(10px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

export const PlayerContainer = styled.div`
   background: #ffffff;
   border-radius: 8px;
   box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
   animation: ${slideIn} 0.3s ease-out;
   width: 100%;
   max-width: 100%;
   max-height: 65vh;
`;
PlayerContainer.displayName = 'PlayerContainer';

const PlayerContainerId = 'playerDivId';

// the analytics API reports a query without any data with this status code
const NO_DATA_STATUS_CODE = 2000;

// kept below the shortest polling interval so a stalled request cannot pile up behind a refetch
const REQUEST_TIMEOUT_MS = 12000;

/**
 * React Query hands out an AbortSignal, while axios 0.21 only understands its own cancel token, so the
 * signal is bridged onto one. Without it a request outlives an unmount or a stream switch.
 */
function getRequestConfig(header, signal) {
   const source = axios.CancelToken.source();
   if (signal) {
      signal.addEventListener('abort', () => source.cancel());
   }
   return { headers: header, timeout: REQUEST_TIMEOUT_MS, cancelToken: source.token };
}

const ENV_CONFIG = {
   dev: {
      ctsDomain: 'token-dev.nanostream.cloud',
      serverDomain: 'bintu-play-dev.nanocosmos.de',
      bintuDomain: 'https://bintu-dev-k8s.nanocosmos.de',
   },
   staging: {
      ctsDomain: 'token-staging.nanostream.cloud',
      serverDomain: 'bintu-play-stage.nanocosmos.de',
      bintuDomain: 'https://bintu-staging.nanocosmos.de',
   },
   prod: {
      ctsDomain: 'token.nanostream.cloud',
      serverDomain: 'bintu-play.nanocosmos.de',
      bintuDomain: 'https://bintu.nanocosmos.de',
   },
};

function inferEnvFromCtsUrl(ctsDomain) {
   if (ctsDomain.includes('dev')) return 'dev';
   if (ctsDomain.includes('staging') || ctsDomain.includes('stage')) return 'staging';
   return 'prod';
}

function resolveEnvConfig(env) {
   return ENV_CONFIG[env] ?? ENV_CONFIG[inferEnvFromCtsUrl(CTS_API_URL)];
}

/**
 * The token id is logged to correlate a playback with the token that was used for it. The token itself
 * is a playback credential and is deliberately not logged, and a malformed one (it can come straight
 * from the URL) must not tear down the view.
 */
function getTokenJti(token) {
   try {
      return JSON.parse(atob(token.split('.')[1])).jti;
   } catch (error) {
      console.warn('Could not decode the playback token: ', error.message);
      return 'undecodable';
   }
}

/**
 * The resolution is a property of the ingest rather than a measurement, so it is read once from the
 * stream info the player reports when playback starts. Audio only streams have no videoInfo.
 */
function getStreamResolution(streamInfo) {
   const videoInfo = streamInfo?.videoInfo;
   if (!streamInfo?.haveVideo || !videoInfo?.width || !videoInfo?.height) return '';
   return `${videoInfo.width}x${videoInfo.height}`;
}

// nanoplayer reports errors as { data: { code, message } }, setup() rejects with a comparable object
function getPlayerErrorMessage(error) {
   const details = error?.data ?? error ?? {};
   return details.message || (details.code ? `error code ${details.code}` : 'unknown player error');
}

function getPlayerConfig(stream, token, serverDomain, bintuDomain, isMoqEnabled, handlers = {}) {
   const { onStats, onError, onStreamInfo } = handlers;
   return {
      source: {
         // defaults: {
         //    service: 'bintu',
         // },
         general: {
            serverDomain,
         },
         // options: {
         //    adaption: {
         //       rule: 'deviationOfMean2',
         //       downStep: 1,
         //    },
         //    switch: {
         //       method: 'server',
         //       pauseOnError: false,
         //       forcePlay: true,
         //       fastStart: false,
         //       timeout: 20,
         //    },
         // },
         startIndex: 0,
         group: {
            id: stream,
            apiurl: bintuDomain,
            // only secure playouts carry a token - a 'security' block with an empty jwtoken
            // makes the player build a secure playout url and fail
            ...(token ? { security: { jwtoken: token } } : {}),
         },
      },
      events: {
         onPause: function (e) {
            console.log('Player paused:', e.data.reason);
         },
         onError: function (e) {
            console.log('error => ', e);
            if (onError) onError(e);
         },
         onWarning: function (e) {
            console.warn('Player warning:', e.data.message);
         },
         ...(onStats ? { onStats } : {}),
         // both events carry the same payload, the update one fires when the ingest changes its format
         ...(onStreamInfo ? { onStreamInfo, onStreamInfoUpdate: onStreamInfo } : {}),
      },
      playback: {
         autoplay: true,
         automute: true,
         muted: false,
         volume: 0.2,
         enableMediaOverQuic: isMoqEnabled,
      },
      // the control bar hides itself and fades back in on mouse interaction, so it costs no space.
      // it is also the only way to unmute a playback that autoplay muted, or to pause one.
      style: { controls: true, width: 'auto', height: 'auto' },
      metrics: {
         accountId: 'nanocosmos3',
         accountKey: 'nc3tb9eq0bnjburu',
         userId: 'demo1',
         eventId: 'analytics-stream-alert-watch',
         statsInterval: 10,
      },
   };
}

function StreamPlayer({ location }) {
   const [playerStats, setPlayerStats] = useState(null);
   const [streamResolution, setStreamResolution] = useState('');
   const [playerError, setPlayerError] = useState('');
   const [ingestHistory, setIngestHistory] = useState(null);
   const [token, setToken] = useState(undefined);
   const [stream, setStream] = useState(undefined);
   const [errorMessageGET, setErrorMessageGET] = useState('');
   const [rtmpStats, setRtmpStats] = useState(null);

   const params = Object.fromEntries(new URLSearchParams(location.search));
   const streamParam = params.streams !== undefined && params.streams.length > 0 ? params.streams : '';
   const orga = params.orga ? params.orga : '';
   const envConfig = resolveEnvConfig(params.env);
   const { ctsDomain, serverDomain, bintuDomain } = envConfig;
   const isMoqEnabled = params.useMoq !== undefined ? params.useMoq === 'true' : true;
   const jwtParam = params.jwt ?? null;

   // fully derived from the URL, so it needs no state
   const missingStreamMessage = streamParam
      ? ''
      : 'No stream provided via URL parameters ( e.g. /streamPlayer?streams=XXXXX-YYYYY).';

   const accessToken = browserStorage.getJwtToken();
   const isAdmin = browserStorage.getUserInfo().isAdmin;

   const header = {
      'x-access-token': accessToken,
      Accept: 'application/json',
      'Content-Type': 'application/json;charset=UTF-8',
      ...(isAdmin && orga ? { 'x-orga-hash': orga } : {}),
   };

   useEffect(() => {
      if (jwtParam) {
         console.log('Set jwt via URL, token jti => ', getTokenJti(jwtParam));
         setToken(jwtParam);
         setStream(streamParam);
      }
   }, [jwtParam, streamParam]);

   useQuery({
      // the token is minted per environment, so the domains belong into the key
      queryKey: ['streamWatch/playerConfig', streamParam, ctsDomain, bintuDomain],
      enabled: streamParam.length > 0 && !jwtParam,
      queryFn: ({ signal }) => {
         return axios
            .get(
               `${ANALYTICS_API_URL}/streamWatch/playerConfig?streams=${streamParam}&ctsDomain=${ctsDomain}&bintuDomain=${bintuDomain}`,
               getRequestConfig(header, signal),
            )
            .then((res) => res.data);
      },
      onSuccess: (data) => {
         const { token, stream } = data;
         // streams of non secure organisations have no token
         if (token) {
            console.log('token jti => ', getTokenJti(token));
         }
         setErrorMessageGET('');
         setToken(token ?? null);
         setStream(stream);
      },
      onError: (error) => {
         // a cancelled request is not a failure, it means the view moved on
         if (axios.isCancel(error)) return;
         const errorObject = getUnifiedErrorObject(error);
         setErrorMessageGET(`Something unexpected happened: ${errorObject.message}`);
         setToken(undefined);
         setStream(undefined);
      },
      refetchOnWindowFocus: false,
      // without this token there is no playback at all, so one retry on a timeout is worth it
      retry: 1,
   });

   useQuery({
      queryKey: ['rtmp/latestMetrics', streamParam],
      enabled: stream !== undefined,
      queryFn: ({ signal }) => {
         return axios
            .get(
               `${ANALYTICS_API_URL}/v2/monitoring/ingest/rtmp/latestMetrics?streams=${streamParam}`,
               getRequestConfig(header, signal),
            )
            .then((res) => res.data);
      },
      onSuccess: (data) => {
         const { timestamp, metrics } = data?.data ?? {};
         // an offline stream has no live metrics anymore - drop the previous ones instead of
         // leaving them on screen as if they were current
         if (data?.statusCode === NO_DATA_STATUS_CODE || !timestamp) {
            setRtmpStats(null);
            return;
         }
         setRtmpStats({
            timestamp: moment.utc(timestamp * 1000),
            bitrate: metrics?.bitrate?.video?.kbits ?? 0,
            maxFrameDelay: metrics?.maxFrameDelay?.milliseconds ?? 0,
            streamTimeRatio: metrics?.streamTimeRatio?.ratio ?? 0,
            elapsedTime: metrics?.elapsedTime?.seconds ?? 0,
         });
      },
      onError: (error) => {
         // const errorObject = getUnifiedErrorObject(error);
         // console.log('rtmp stats fetching error: ', errorObject.message);
         setRtmpStats(null);
      },
      refetchInterval: 15000,
      refetchOnWindowFocus: false,
   });

   // Last 15 minutes of raw ingest metrics from the troubleshooting timeseries API, polled every 30s.
   // Raw (unaggregated) datapoints feed the per-metric graphs below the current values.
   useQuery({
      queryKey: ['ingestHistory', streamParam],
      enabled: stream !== undefined,
      queryFn: async ({ signal }) => {
         const from = encodeURIComponent(moment.utc().subtract(15, 'minutes').toISOString());
         const to = encodeURIComponent(moment.utc().toISOString());
         const query = `from=${from}&to=${to}&streams=${streamParam}`;
         const base = `${ANALYTICS_API_URL}/v2/troubleshooting/rtmp`;
         const requestConfig = getRequestConfig(header, signal);

         const [bitrate, maxFrameDelay, streamTimeRatio] = await Promise.all([
            axios.get(`${base}/video/bitrate/raw/timeseries?${query}`, requestConfig).then((res) => res.data),
            axios.get(`${base}/video/maxFrameDelay/raw/timeseries?${query}`, requestConfig).then((res) => res.data),
            axios.get(`${base}/streamTimeRatio/raw/timeseries?${query}`, requestConfig).then((res) => res.data),
         ]);

         return {
            bitrate: (bitrate.data || []).map((item) => ({ t: item.timestamp * 1000, v: item.bitrate?.kbps })),
            maxFrameDelay: (maxFrameDelay.data || []).map((item) => ({
               t: item.timestamp * 1000,
               v: item.maxFrameDelay,
            })),
            streamTimeRatio: (streamTimeRatio.data || []).map((item) => ({
               t: item.timestamp * 1000,
               v: item.streamTimeRatio,
            })),
         };
      },
      onSuccess: (data) => setIngestHistory(data),
      onError: () => setIngestHistory(null),
      refetchInterval: 30000,
      refetchOnWindowFocus: false,
      retry: false,
   });

   // the realtime alerting endpoint has no stream filter, it answers with the whole snapshot of an organization.
   // without 'x-orga-hash' an admin request would pull the alerts of every organization, so scope it to the
   // organization of the played stream.
   const alertingOrgaHash = orga || getOrgaHashOfStream(streamParam);
   const alertingHeader = { ...header, ...(isAdmin && alertingOrgaHash ? { 'x-orga-hash': alertingOrgaHash } : {}) };

   const { data: alertingData, isError: alertingFailed } = useQuery({
      queryKey: ['alerting/ingest', alertingOrgaHash],
      enabled: stream !== undefined,
      queryFn: ({ signal }) => {
         return axios
            .get(`${ANALYTICS_API_URL}/v2/alerting/ingest`, getRequestConfig(alertingHeader, signal))
            .then((res) => res.data);
      },
      // the alerting data is only recalculated once per minute and lags a few minutes behind
      refetchInterval: 60000,
      refetchOnWindowFocus: false,
      retry: false,
   });

   const streamAlerts = useMemo(() => selectStreamAlerts(alertingData, stream), [alertingData, stream]);
   const alerting = stream
      ? { alerts: streamAlerts, snapshotTime: alertingData?.timestampAsString, isError: alertingFailed }
      : undefined;

   // The rendition ladder is a static property of the stream group, so it is fetched once and not polled.
   // bintu is a separate service and this route is public, so the analytics session header must not be sent.
   const { data: streamGroup, isError: streamGroupFailed } = useQuery({
      queryKey: ['bintu/streamGroup', bintuDomain, stream],
      enabled: stream !== undefined,
      queryFn: ({ signal }) => {
         return axios
            .get(`${bintuDomain}/stream/${stream}/group`, getRequestConfig(undefined, signal))
            .then((res) => res.data);
      },
      refetchOnWindowFocus: false,
      retry: 1,
   });

   const renditions = useMemo(() => selectRenditions(streamGroup, stream), [streamGroup, stream]);

   // the instance is owned by this effect instead of by state, so a changed config (stream, token, env)
   // destroys the old player and sets up a new one
   useEffect(() => {
      if (!stream) return undefined;

      if (!window.NanoPlayer) {
         setPlayerError('The nanoplayer library could not be loaded, playback is unavailable.');
         return undefined;
      }

      // a new player means a new stream, so nothing of the previous playback may stay on screen
      setPlayerError('');
      setPlayerStats(null);
      setStreamResolution('');
      const playerInstance = new window.NanoPlayer(PlayerContainerId);
      const config = getPlayerConfig(stream, token, serverDomain, bintuDomain, isMoqEnabled, {
         onStats: (event) => {
            // Playback stats reported by the player (receiver side).
            const stats = event?.data?.stats;
            if (stats) {
               setPlayerStats({
                  latency: stats.buffer?.delay?.avg,
                  framerate: stats.framerate?.avg,
               });
            }
         },
         onError: (event) => setPlayerError(`Playback failed: ${getPlayerErrorMessage(event)}`),
         onStreamInfo: (event) => setStreamResolution(getStreamResolution(event?.data?.streamInfo)),
      });
      console.log('Player config => ', JSON.stringify(config));
      playerInstance.setup(config).catch((error) => {
         console.error(error);
         setPlayerError(`Playback could not be started: ${getPlayerErrorMessage(error)}`);
      });

      return () => {
         playerInstance.destroy().catch((error) => console.error(error));
      };
   }, [stream, token, serverDomain, bintuDomain, isMoqEnabled]);

   return (
      <Container>
         <PlayerContainer id={PlayerContainerId} />
         {[missingStreamMessage, errorMessageGET, playerError].filter(Boolean).map((message) => (
            <Notification key={message}>{message}</Notification>
         ))}
         <StreamMetricsDisplay
            inputMetrics={rtmpStats}
            stream={stream}
            playerStats={playerStats}
            resolution={streamResolution}
            history={ingestHistory}
            alerting={alerting}
            transcodes={stream ? { renditions, isError: streamGroupFailed } : undefined}
         />
      </Container>
   );
}

StreamPlayer.propTypes = {
   location: PropTypes.object,
};

export default StreamPlayer;
